import { CheckboxField, useFormContext, useFormField } from '@calendly/form';
import { FieldError, useErrorId } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import { TextInput } from '@calendly/ui/components/text-input';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { colorTextSecondary } from '@calendly/ui/theme';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventTypeContext } from 'features/event_type_provider';
import { shouldShowGroupEventControls } from '../../is_event_type_editor_crud_eligible';

// Form values are strings; API has no unlimited — 9999 is the "no limit" sentinel.
const API_UNLIMITED_INVITEES_LIMIT = '9999';
const DEFAULT_INVITEES_LIMIT_WHEN_ENABLED = '10';
const headerCss = "h11zc3ia";
const switchRowCss = "slmfawt";
const switchInputWrapCss = "s4snnq";
const expandedCss = "e1lia2ky";
const limitBlockCss = "lzqzbc6";
const limitRowCss = "l7n4ehg";
const limitInputCss = "l11ns8m8";
const InviteesLimitField = ({
  disabled,
  ariaLabel,
  suffixLabel
}) => {
  const [fieldProps, meta, {
    setValue,
    setTouched,
    setError
  }] = useFormField({
    name: 'inviteesLimit'
  });
  const showError = Boolean(meta.touched && meta.error);
  const errorId = useErrorId({
    error: showError ? meta.error : undefined
  });
  const handleChange = useCallback(event => {
    setTouched(true);
    setError(undefined);
    setValue(event.target.value);
  }, [setError, setTouched, setValue]);
  return <div className={limitBlockCss} data-testid="invitees-limit-field">
      <div className={limitRowCss}>
        <TextInput {...fieldProps} aria-describedby={errorId} aria-invalid={showError} aria-label={ariaLabel} autoComplete="off" autoCorrect="off" className={limitInputCss} disabled={disabled} onChange={handleChange} spacingBottom={0} spacingTop={0} type="text" />
        <BodyText size="medium">{suffixLabel}</BodyText>
      </div>
      {showError && <FieldError id={errorId} text={meta.error} />}
    </div>;
};
const spotsHelperCss = "s1le1yx0";
const sectionCss = "s1wycr7a";
const isUnlimitedLimit = value => String(value) === API_UNLIMITED_INVITEES_LIMIT;
export const GroupEventSection = ({
  isLocked = false
}) => {
  const {
    eventType
  } = useEventTypeContext();
  const {
    values,
    setFieldValue,
    setFieldTouched,
    validateField
  } = useFormContext();
  const previousLimitRef = useRef(null);
  const showControls = shouldShowGroupEventControls(eventType);
  const inviteesLimit = values.inviteesLimit;
  const [limitEnabled, setLimitEnabled] = useState(() => !isUnlimitedLimit(inviteesLimit));
  useEffect(() => {
    if (!showControls || !limitEnabled || isLocked) return;
    validateField('inviteesLimit');
    setFieldTouched('inviteesLimit');
  }, [showControls, limitEnabled, isLocked, setFieldTouched, validateField]);
  const handleToggle = useCallback(() => {
    if (isLocked) return;
    if (limitEnabled) {
      previousLimitRef.current = inviteesLimit;
      setFieldValue('inviteesLimit', API_UNLIMITED_INVITEES_LIMIT);
      setFieldValue('displayGroupOccupancy', false);
      setFieldTouched('displayGroupOccupancy');
      setLimitEnabled(false);
      return;
    }
    const restored = previousLimitRef.current && !isUnlimitedLimit(previousLimitRef.current) ? previousLimitRef.current : DEFAULT_INVITEES_LIMIT_WHEN_ENABLED;
    setFieldValue('inviteesLimit', String(restored));
    setFieldTouched('inviteesLimit');
    setLimitEnabled(true);
  }, [inviteesLimit, isLocked, limitEnabled, setFieldTouched, setFieldValue]);
  if (!showControls) return null;
  const switchId = 'group-size-limit-switch';
  const switchLabel = I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.limit_toggle');
  return <div className={sectionCss} data-testid="group-event-section">
      <div className={headerCss}>
        <LabelText as="h2" size="large">
          {I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.title')}
        </LabelText>
        <BodyText color="muted" size="medium">
          {I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.subtitle')}
        </BodyText>
      </div>

      <label className={switchRowCss} htmlFor={switchId}>
        <span className={switchInputWrapCss}>
          <SwitchInput id={switchId} checked={limitEnabled} disabled={isLocked} aria-label={switchLabel} onChange={handleToggle} spacingBottom={0} spacingTop={0} />
        </span>
        <BodyText size="medium">{switchLabel}</BodyText>
      </label>

      {limitEnabled && <div className={expandedCss} data-testid="group-event-limit-fields">
          <InviteesLimitField ariaLabel={I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.invitees_max')} disabled={isLocked} suffixLabel={I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.invitees_max')} />

          <div>
            <CheckboxField name="displayGroupOccupancy" label={I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.show_spots_remaining')} disabled={isLocked} data-testid="display-group-occupancy-checkbox" />
            <div className={spotsHelperCss}>
              <BodyText color={colorTextSecondary} size="small">
                {I18n.t('publisher.event_type_editor_crud.meeting_details.group_size.show_spots_remaining_helper')}
              </BodyText>
            </div>
          </div>
        </div>}
    </div>;
};

require("./group_event_section.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./group_event_section.js");